import React from 'react';
import { Alert, Button, Space, Tag, Tooltip } from 'antd';

import { AssessmentRegeneration } from './api';

const STATUS_COPY = {
  queued: {
    title: 'Answer key derivation queued',
    detail:
      'Your changes are saved. The answer key is derived again automatically before this version can launch.',
    tone: 'info',
    color: 'processing',
  },
  running: {
    title: 'Deriving the answer key',
    detail:
      'The answer key is being derived from your changes and put through its quality audit. Launch stays unavailable until it is ready.',
    tone: 'info',
    color: 'processing',
  },
  ready: {
    title: 'Answer key matches this version',
    detail:
      'The answer key was derived from what this version holds now. Saving further edits derives it again automatically.',
    tone: 'success',
    color: 'success',
  },
  failed: {
    title: 'The answer key could not be derived',
    detail:
      'Your changes are saved. Derive it again before launching this version.',
    tone: 'error',
    color: 'error',
  },
  blocked: {
    title: 'The answer key needs a decision',
    detail:
      'The answer key did not pass its checks. Resolve what is reported below before launching this version.',
    tone: 'warning',
    color: 'warning',
  },
  idle: {
    title: 'The answer key follows your edits',
    detail:
      'Saving a change to the exercise derives the answer key again automatically.',
    tone: 'info',
    color: 'default',
  },
} as const;

const AssessmentRegenerationStatus: React.FC<{
  regeneration?: AssessmentRegeneration;
  onRetry?: () => void;
  retrying?: boolean;
  compact?: boolean;
}> = ({ regeneration, onRetry, retrying, compact }) => {
  if (!regeneration?.automatic) return null;
  const copy = STATUS_COPY[regeneration.status] || STATUS_COPY.idle;
  const detail = regeneration.message || copy.detail;
  const canRetry =
    regeneration.retryable &&
    ['failed', 'blocked', 'idle'].includes(regeneration.status) &&
    onRetry;
  const retry = canRetry ? (
    <Button size="small" onClick={onRetry} loading={retrying}>
      {regeneration.status === 'idle'
        ? 'Derive the answer key'
        : 'Derive the answer key again'}
    </Button>
  ) : null;

  if (compact) {
    return (
      <Space size={6} wrap aria-live="polite">
        <Tooltip title={detail}>
          <Tag color={copy.color}>{copy.title}</Tag>
        </Tooltip>
        {retry}
      </Space>
    );
  }

  return (
    <Alert
      className="assessment-callout"
      type={copy.tone}
      showIcon
      title={copy.title}
      description={detail}
      action={retry}
    />
  );
};

export default AssessmentRegenerationStatus;
